import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import LocationSixAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/locations/location-six/location-six.entity';
import LocationOneAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/locations/location-one/location-one.entity';
import LocationTwoAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/locations/location-two/location-two.entity';
import LocationThreeAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/locations/location-three/location-three.entity';
import LocationFourAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/locations/location-four/location-four.entity';
import LocationFiveAttributes from '@albatrosdeveloper/ave-models-package/lib/schemas/locations/location-five/location-five.entity';
import {
  andAllWhere,
  andWhere,
  buildQuery,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,
} from '@albatrosdeveloper/ave-utils-npm/lib/utils/query.util';
import { HttpClient } from '@angular/common/http';
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from '../../../../common/interfaces';
import { ICreateLocationSix } from './interfaces/create-location-six.interface';
import { IUpdateLocationSix } from './interfaces/update-location-six.interface';
import { environment as env } from '../../../../../environments/environment.prod';
import { StatusEnum } from '@albatrosdeveloper/ave-models-package/lib/enums';
import { AbstractService } from '../../../../services/abstract.service';

const apiUrl = env.API_LOCATION_URL;
const moduleName = 'location_six';

@Injectable({
  providedIn: 'root',
})
export class LocationSixService extends AbstractService<
  LocationSixAttributes,
  ICreateLocationSix,
  IUpdateLocationSix
> {
  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  /**
 * Pagination
 * @param options Pagination options
 * @param locationOneCodeList location-one codes filter
 * @param locationTwoCodeList location-two codes filter
 * @param locationThreeCodeList location-three codes filter
 * @param locationFourCodeList location-four codes filter
 * @param locationFiveCodeList location-five codes filter
 * @returns Paginated data
 */
  findAllWithCount(
    options: IPagination<LocationSixAttributes>,
    locationOneCodeList: string[],
    locationTwoCodeList: string[],
    locationThreeCodeList: string[],
    locationFourCodeList: string[],
    locationFiveCodeList: string[]
  ): Observable<IPaginationResult<LocationSixAttributes>> {

    const query = buildQuery<LocationSixAttributes>(
      andWhere('_deleted', Ops.not(true)),
      options.search ? andWhere("code", Ops.like(options.search ?? "", "i")) : noop(),
      options.search ? orWhere("name", Ops.like(options.search ?? "", "i")) : noop(),
      locationOneCodeList && locationOneCodeList.length > 0 ? where('locationOne.code' as keyof LocationSixAttributes, Ops.in(...locationOneCodeList)) : noop(),
      locationTwoCodeList && locationTwoCodeList.length > 0 ? where('locationTwo.code' as keyof LocationSixAttributes, Ops.in(...locationTwoCodeList)) : noop(),
      locationThreeCodeList && locationThreeCodeList.length > 0 ? where('locationThree.code' as keyof LocationSixAttributes, Ops.in(...locationThreeCodeList)) : noop(),
      locationFourCodeList && locationFourCodeList.length > 0 ? where('locationFour.code' as keyof LocationSixAttributes, Ops.in(...locationFourCodeList)) : noop(),
      locationFiveCodeList && locationFiveCodeList.length > 0 ? where('locationFive.code' as keyof LocationSixAttributes, Ops.in(...locationFiveCodeList)) : noop(),
      orderBy(
        options.sort ?? "code",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<LocationSixAttributes>>(API_URL);
  }


  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(): Observable<IRequestResult<LocationSixAttributes[]>> {
    const query = buildQuery<LocationSixAttributes>(
      where('statusId', StatusEnum.ACTIVE),
      select(['code', 'name']),
      orderBy('code', OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<LocationSixAttributes[]>>(API_URL);
  }
}
